import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import AssistantDirectionRoundedIcon from '@mui/icons-material/AssistantDirectionRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { cardColor } from '../pages/jogos/GameCard';
import tokens from '../theme/tokens';

// Lances da partida registrados pelo árbitro ou pela diretoria: gol, assistência e cartões.
export const CORES_TIMES = [tokens.color.team1, tokens.color.team2];
export const CARTOES = ['amarelo', 'azul', 'vermelho'];
export const ACOES_LANCE = [
  { tipo: 'gol', label: 'Gol', cor: tokens.color.success },
  { tipo: 'assistencia', label: 'Assistência', cor: tokens.color.info },
  { tipo: 'amarelo', label: 'Amarelo', cor: cardColor.amarelo },
  { tipo: 'azul', label: 'Azul', cor: cardColor.azul },
  { tipo: 'vermelho', label: 'Vermelho', cor: cardColor.vermelho },
];
export const labelLance = (tipo) => (ACOES_LANCE.find((a) => a.tipo === tipo) || {}).label || tipo;

export const nomeCurto = (nome = '') => nome.replace(/\s*\(.*\)\s*/, ' ').trim().split(/\s+/).slice(0, 2).join(' ');

export const Cartao = ({ cor, size = 14 }) => (
  <Box component="span" sx={{ display: 'inline-block', width: size * 0.75, height: size, borderRadius: 0.5, bgcolor: cor, verticalAlign: 'middle' }} />
);

// Ícone de um lance (bola, assistência ou cartão da cor)
export const IconeLance = ({ tipo, size = 'small' }) => {
  if (tipo === 'gol') return <SportsSoccerRoundedIcon fontSize={size} sx={{ color: tokens.color.success }} />;
  if (tipo === 'assistencia') return <AssistantDirectionRoundedIcon fontSize={size} sx={{ color: tokens.color.info }} />;
  return <Cartao cor={cardColor[tipo]} />;
};

/** Lances de um jogador num time da partida. */
export function contarLances(eventos = [], time, nome) {
  const doJogador = eventos.filter((e) => e.time === time && e.user_name === nome);
  return {
    eventos: doJogador,
    gols: doJogador.filter((e) => e.tipo === 'gol').length,
    assistencias: doJogador.filter((e) => e.tipo === 'assistencia').length,
    cartoes: doJogador.filter((e) => CARTOES.includes(e.tipo)).map((e) => e.tipo),
  };
}

// Marcadores ao lado do nome: cartões, gols e assistências
export const MarcadoresLance = ({ contagem }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
    {contagem.cartoes.map((tipo, i) => <Cartao key={i} cor={cardColor[tipo]} />)}
    {contagem.gols > 0 && <Chip size="small" icon={<SportsSoccerRoundedIcon />} label={contagem.gols} sx={{ height: 24, fontWeight: 700 }} />}
    {contagem.assistencias > 0 && (
      <Chip size="small" icon={<AssistantDirectionRoundedIcon />} label={contagem.assistencias} sx={{ height: 24, fontWeight: 700 }} title="Assistências" />
    )}
  </Stack>
);

/**
 * Diálogo de lances de um jogador: gol, assistência ou cartão (amarelo, azul, vermelho) e,
 * opcionalmente, os lances dele na partida com a opção de desfazer.
 * `jogador`: { time, user_name } ou null.
 */
export const LanceDialog = ({ jogador, onLance, onClose, eventos = [], onDesfazer, enviando }) => (
  <Dialog open={Boolean(jogador)} onClose={onClose} fullWidth maxWidth="xs">
    {jogador && (
      <>
        <DialogTitle sx={{ borderLeft: `6px solid ${CORES_TIMES[jogador.time]}` }}>
          {nomeCurto(jogador.user_name)}
          <Typography variant="body2" color="text.secondary">Time {jogador.time + 1}</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            {ACOES_LANCE.map((a) => {
              const destaque = a.tipo === 'gol' || a.tipo === 'assistencia';
              return (
                <Grid item xs={destaque ? 6 : 4} key={a.tipo}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={enviando}
                    onClick={() => onLance(a.tipo)}
                    startIcon={a.tipo === 'gol' ? <SportsSoccerRoundedIcon /> : a.tipo === 'assistencia' ? <AssistantDirectionRoundedIcon /> : <Cartao cor="#fff" size={18} />}
                    sx={{ py: destaque ? 2.5 : 2, fontSize: destaque ? '1.05rem' : '0.95rem', bgcolor: a.cor, color: a.tipo === 'amarelo' ? '#111' : '#fff', '&:hover': { bgcolor: a.cor, filter: 'brightness(0.92)' } }}
                  >
                    {a.label}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
          {onDesfazer && eventos.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Lances dele nesta partida</Typography>
              <Stack spacing={0.5}>
                {eventos.map((e) => (
                  <Stack key={e._id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.25, px: 1, borderRadius: 1.5, bgcolor: 'background.default' }}>
                    <IconeLance tipo={e.tipo} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{labelLance(e.tipo)}</Typography>
                    <Tooltip title="Desfazer">
                      <span>
                        <IconButton size="small" onClick={() => onDesfazer(e)} disabled={enviando}><UndoRoundedIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">Fechar</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
);
